import { Course, Product } from '@/App.interfaces';
import { StatsigEvent, useStatsigClient } from '@statsig/react-bindings';
import cn from 'classnames';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useApp } from '../helpers/api-helpers';
import { sendGAEvent, StatsigClientType } from '../helpers/stat-helpers';
import Button from './Button';
import styles from './PurchaseCTA.module.css';

type PurchaseCTAData = {
  /** Course being viewed by the user */
  course: Course | null;
  /** Product to offer if not already subscribed */
  product: Product | null;
  /** Dialog or app feature to attribute the click to */
  source: string | null;
};

type PurchaseCTAContextType = PurchaseCTAData & {
  /** Whether or not the Call-To-Action should be visible */
  visible: boolean;
  /** Function to set product & course data on the context */
  setup: React.Dispatch<React.SetStateAction<PurchaseCTAData>>;
  /** Function to hide the Call-To-Action's visibility */
  hide: () => void;
  /** Function to show the Call-To-Action's visibility */
  show: () => void;
};

export const PurchaseCTAContext: React.Context<
  PurchaseCTAContextType | undefined
> = React.createContext<PurchaseCTAContextType | undefined>(undefined);

export function usePurchaseCTA(): PurchaseCTAContextType | undefined {
  const context = useContext(PurchaseCTAContext);
  return context;
}

type PurchaseCTAProviderProps = {
  product?: Product | null;
};

export function PurchaseCTAProvider({
  children,
  ...initialData
}: React.PropsWithChildren<PurchaseCTAProviderProps>) {
  const [data, setData] = useState<PurchaseCTAData>({
    course: null,
    product: initialData?.product || null,
    source: null,
  });
  const [visible, setVisible] = useState(true);
  const context = useMemo(
    () => ({
      ...data,
      visible,
      show: () => {
        if (!visible) {
          setVisible(true);
        }
      },
      hide: () => {
        if (visible) {
          setVisible(false);
        }
      },
      setup: setData,
    }),
    [data, visible],
  );

  return (
    <PurchaseCTAContext.Provider value={context}>
      {children}
    </PurchaseCTAContext.Provider>
  );
}

export function PurchaseCTA({ className }: { className: string }) {
  const { openProductCheckout } = useApp();
  const { source, course, product, visible } = usePurchaseCTA() || {};
  const client = useStatsigClient();

  const onClick = useCallback(() => {
    sendGAEvent('ClickCTA', {
      event_category: source || 'UnknownSource',
      event_label: course?.name || 'UnknownCourse',
      source: source || 'UnknownSource',
      course: course?.name || 'UnknownCourse',
      product: product?.name || 'UnknownProduct',
    });

    const event: StatsigEvent = {
      eventName: 'preview_enrollment_subscribe_button_click',
      value: 1,
      metadata: {
        course_name: course?.name,
        course_id: course?.id,
        product_name: product?.name,
        product_id: product?.id,
        source: source ?? undefined,
      },
    };
    client.logEvent(event);
    if (product && course && openProductCheckout) {
      openProductCheckout(product.id, { course: course.id });
    }
  }, [source, course, product, client, openProductCheckout]);

  if (!product?.id || !visible) {
    return null;
  }

  return (
    <div className={cn(styles.wrapper, className)}>
      {product &&
      course &&
      (getPurchaseCTAButtonPlacement(client) === 'left' ||
        getPurchaseCTAButtonPlacement(client) === 'top') ? null : (
        <>
          <span className={styles.label}>Ready for Full Access?</span>
          <Button type="" className={styles.button} onClick={onClick}>
            Subscribe
          </Button>
        </>
      )}
    </div>
  );
}

// This allows non functional components to easily suppress the purchase cta
export function PurchaseCTASuppressor({ children }: React.PropsWithChildren) {
  const purchaseCTA = usePurchaseCTA();
  useEffect(() => {
    purchaseCTA?.hide();
    return () => purchaseCTA?.show();
  }, [purchaseCTA]);

  return <PurchaseCTAProvider {...purchaseCTA}>{children}</PurchaseCTAProvider>;
}

/**
 * **TEMPORARY**: Checks the Statsig Subscribe-button experiment to determine what Subscribe button to show users.
 *
 * This function checks the experiment's configuration and determines the position of the Subscribe button on the page.
 * It can return one of the following values:
 *
 * - **'left'**: The Subscribe button appears in the lefthand navigation pane.
 * - **'top'**: The Subscribe button appears at the top of the course page.
 * - **'corner'**: The Subscribe button remains in the top-right corner of the page (the control group).
 * - **'unavailable'**: The Statsig client is not ready to determine the button placement.
 *
 * @param client - The Statsig client instance used to fetch the experiment configuration.
 * @returns The placement of the Subscribe button or 'unavailable' if the Statsig client isn't ready.
 */
export function getPurchaseCTAButtonPlacement(
  client: StatsigClientType,
): 'left' | 'top' | 'corner' | 'unavailable' {
  const buttonPlacement = client
    .getExperiment('enhanced_subscribe_button_visibility')
    .get('button_placement', null);

  if (!buttonPlacement) {
    return 'unavailable';
  }

  if (
    buttonPlacement === 'left' ||
    buttonPlacement === 'top' ||
    buttonPlacement === 'corner'
  ) {
    return buttonPlacement;
  }
  return 'corner';
}
